import React, { useEffect, useState } from 'react';
import {
  Edit,
  SimpleForm,
  useDataProvider,
  useEditController,
  useRecordContext,
  Toolbar,
  SaveButton,
  DeleteButton, 
} from 'react-admin';
import { Box, Button, Snackbar, Alert } from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import ConfigsFormFields from '../customForms/ConfigsFormFields';

const CustomToolbar = ({ onUploadClick, uploading }) => {
  const fileInputRef = React.useRef();

  return (
    <>
      <input
        type="file"
        multiple
        hidden
        ref={fileInputRef}
        onChange={onUploadClick}
      />
      <Toolbar
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 100,
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SaveButton />
          <Button
            variant="contained"
            color="secondary"
            startIcon={<UploadIcon />}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={uploading}
          >
            {uploading ? 'A enviar...' : 'Adicionar ficheiros'}
          </Button>
        </Box>
        <DeleteButton mutationMode="pessimistic" label="Apagar" />
      </Toolbar>
    </>
  );
};

// ✅ Custom title component using record.name
const ConfigsTitle = () => {
  const record = useRecordContext();
  return <span>Configuração {record ? `${record.name || 'sem nome'}` : ''}</span>;
};

const ConfigsEdit = () => {
  const dataProvider = useDataProvider();
  const { record, isLoading, save, ...controllerProps } = useEditController();

  const [fileOptions, setFileOptions] = useState({ Pdfs: [], Images: [], Videos: [] });
  const [companyID, setCompanyID] = useState(undefined);
  const [allConfigs, setAllConfigs] = useState([]);
  const [backgroundImages, setBackgroundImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const result = await dataProvider.listFiles();
        const allFiles = [...(result.Pdfs || []), ...(result.Images || []), ...(result.Videos || [])];
        setFileOptions({
          Pdfs: allFiles.filter(f => f.toLowerCase().endsWith('.pdf')),
          Videos: allFiles.filter(f => f.toLowerCase().endsWith('.mp4')),
          Images: allFiles.filter(f => !f.toLowerCase().endsWith('.pdf') && !f.toLowerCase().endsWith('.mp4')),
        });
        setCompanyID(result.companyID);
      } catch (err) {
        console.error('Failed to fetch files', err);
      }
    };

    const fetchConfigs = async () => {
      try {
        const configsRes = await dataProvider.getList('configs', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'Name', order: 'ASC' },
          filter: {},
        });
        setAllConfigs(configsRes.data);
      } catch (err) {
        console.error('Failed to fetch configs:', err);
      }
    };

    fetchFiles();
    fetchConfigs();
  }, [dataProvider]);

  useEffect(() => {
    const sortByOrder = (images = []) =>
      [...images].sort(
        (a, b) =>
          (a.sortOrder ?? a.SortOrder ?? 0) -
          (b.sortOrder ?? b.SortOrder ?? 0)
      );

    if (record?.backgroundImages) {
      setBackgroundImages(sortByOrder(record.backgroundImages));
    } else if (record?.images) {
      setBackgroundImages(sortByOrder(record.images));
    } else {
      setBackgroundImages([]);
    }
  }, [record]);

  const moveItemUp = (index) => {
    if (index === 0) return;
    const newImages = [...backgroundImages];
    [newImages[index - 1], newImages[index]] = [newImages[index], newImages[index - 1]];
    setBackgroundImages(newImages);
  };

  const moveItemDown = (index) => {
    if (index === backgroundImages.length - 1) return;
    const newImages = [...backgroundImages];
    [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
    setBackgroundImages(newImages);
  };

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    try {
      setUploading(true);
      await dataProvider.uploadFiles(files);
      const result = await dataProvider.listFiles();
      setFileOptions({
        Pdfs: result.Pdfs || [],
        Images: result.Images || [],
        Videos: result.Videos || [],
      });
      setCompanyID(result.companyID);
    } catch (error) {
      const serverMessage = error?.response?.data?.error;
      const fallbackMessage = error?.message || 'Erro desconhecido';
      const message = `Erro ao enviar os ficheiros: ${serverMessage || fallbackMessage}`;
      setErrorMessage(message);
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
      event.target.value = null;
    }
  };

  return (
    <>
      <Edit {...controllerProps} title={<ConfigsTitle />}>
        <SimpleForm
          toolbar={<CustomToolbar onUploadClick={handleFileChange} uploading={uploading} />}
          warnWhenUnsavedChanges
          sanitizeEmptyValues
          record={{ ...record, backgroundImages }}
        >
          <ConfigsFormFields
            fileOptions={fileOptions}
            companyID={companyID}
            allConfigs={allConfigs}
            moveItemUp={moveItemUp}
            moveItemDown={moveItemDown}
          />
        </SimpleForm>
      </Edit>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setSnackbarOpen(false)}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ConfigsEdit;
